import * as React from "react";
import { useConvex, useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import { api } from "$convex/convexApi";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { NativeSelect } from "@/components/ui/native-select";
import { formatCurrency, formatDate } from "@/lib/format";
import { CampaignClientReview } from "@/pages/marketing/campaigns/CampaignClientReview";
import type {
  ComparisonHistoryRow,
  ComparisonSelection,
  ProposedCoffee,
  SavingsCampaign,
  SavingsProspect,
} from "@/pages/marketing/campaigns/types";

type History = {
  campaign: SavingsCampaign;
  rows: ComparisonHistoryRow[];
  selections: ComparisonSelection[];
  disclaimer: string;
};
type Choice = { key: string; proposedCargoId: string };
const number = (value: number) =>
  value.toLocaleString("en-US", { maximumFractionDigits: 1 });
const sourceKey = (selection: ComparisonSelection) =>
  `${selection.historicalProductId}|${selection.historicalCargoId ?? "no-cargo"}`;
const message = (error: unknown) =>
  error instanceof Error
    ? error.message
    : "The comparison could not be updated. Please try again.";

function ComparisonLines({
  customerId,
  campaignId,
  canManage,
  options,
}: {
  customerId: string;
  campaignId: string;
  canManage: boolean;
  options: ProposedCoffee[] | undefined;
}) {
  const history = useQuery(api.contractSavings.comparisonHistory, {
    campaignId,
    customerId,
  }) as History | undefined;
  if (!history || !options)
    return (
      <p role="status" className="py-6 text-sm text-muted-foreground">
        Loading purchase lines and available coffees…
      </p>
    );
  return (
    <SelectionEditor
      key={`${campaignId}-${customerId}-${history.campaign.lookbackMonths}`}
      history={history}
      options={options}
      customerId={customerId}
      canManage={canManage}
    />
  );
}

function SelectionEditor({
  history,
  options,
  customerId,
  canManage,
}: {
  history: History;
  options: ProposedCoffee[];
  customerId: string;
  canManage: boolean;
}) {
  const [choices, setChoices] = React.useState<Choice[]>(() =>
    history.selections.map((selection) => ({
      key: sourceKey(selection),
      proposedCargoId: selection.proposedCargoId,
    })),
  );
  const [preview, setPreview] = React.useState<SavingsProspect>();
  const [previewError, setPreviewError] = React.useState<string>();
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const request = React.useRef(0);
  const convex = useConvex();
  const save = useMutation(api.contractSavings.saveCustomer);
  const groups = new Map<string | null, ComparisonHistoryRow[]>();
  for (const row of history.rows)
    groups.set(row.originName, [...(groups.get(row.originName) ?? []), row]);
  const missingHistory = choices.some(
    (choice) => !history.rows.some((row) => row.key === choice.key),
  );
  const missingProposal = choices.some(
    (choice) =>
      !options.some((option) => option.cargoId === choice.proposedCargoId),
  );
  const ready =
    choices.length > 0 &&
    choices.length <= 50 &&
    !missingHistory &&
    !missingProposal;
  const previewKey = JSON.stringify({ choices, options, rows: history.rows });
  React.useEffect(() => {
    request.current++;
    setPreview(undefined);
    setPreviewError(undefined);
    setLoading(false);
    setSaved(false);
    return () => {
      request.current++;
    };
  }, [previewKey]);

  function updateChoices(next: Choice[]) {
    request.current++;
    setPreview(undefined);
    setPreviewError(undefined);
    setLoading(false);
    setSaved(false);
    setChoices(next);
  }
  function payload(): ComparisonSelection[] {
    return choices.map((choice) => {
      const row = history.rows.find((row) => row.key === choice.key);
      if (!row)
        throw new Error(
          "A selected purchase line is no longer in this history window. Clear the selection and choose again.",
        );
      return {
        historicalProductId: row.productId,
        historicalCargoId: row.historicalCargoId,
        proposedCargoId: choice.proposedCargoId,
      };
    });
  }
  async function calculate() {
    const current = ++request.current;
    setLoading(true);
    setPreview(undefined);
    setPreviewError(undefined);
    try {
      const result = await convex.query(api.contractSavings.previewCustomer, {
        campaignId: history.campaign._id,
        customerId,
        selections: payload(),
      });
      if (current === request.current) setPreview(result as SavingsProspect);
    } catch (error) {
      if (current === request.current) setPreviewError(message(error));
    } finally {
      if (current === request.current) setLoading(false);
    }
  }
  async function saveComparison() {
    if (!ready || saving) return;
    const current = ++request.current;
    setSaving(true);
    try {
      const args = {
        campaignId: history.campaign._id,
        customerId,
        selections: payload(),
      };
      await save(args);
      setSaved(true);
      toast.success("Selected coffees saved to the campaign draft.");
      try {
        const refreshed = await convex.query(
          api.contractSavings.previewCustomer,
          args,
        );
        if (current === request.current)
          setPreview(refreshed as SavingsProspect);
      } catch (error) {
        if (current === request.current) {
          setPreview(undefined);
          setPreviewError(
            `The comparison was saved, but the current estimate could not be loaded: ${message(error)}`,
          );
        }
      }
    } catch (error) {
      toast.error(message(error));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="space-y-5">
      <div className="rounded-lg border bg-muted/20 p-4 text-sm">
        <p className="font-medium">
          Purchase history for {history.campaign.name}
        </p>
        <p className="mt-1 text-muted-foreground">
          {formatDate(history.campaign.periodStart)} –{" "}
          {formatDate(history.campaign.periodEnd)} ·{" "}
          {history.campaign.lookbackMonths} months · Completed deliveries only
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          This comparison uses the campaign’s history window. Select an origin
          or individual purchase lots, then choose the coffee to propose for
          each line. Three-month demand uses the selected line’s pounds;
          proposed quantities round up to whole bags, starting at one bag.
        </p>
      </div>
      {history.rows.length === 0 ? (
        <p className="py-6 text-sm text-muted-foreground">
          No completed coffee purchases in this campaign window. Choose a
          campaign with a different history window.
        </p>
      ) : (
        [...groups].map(([origin, rows]) => {
          const selected = rows.filter((row) =>
            choices.some((choice) => choice.key === row.key),
          ).length;
          return (
            <details
              key={origin ?? "unassigned"}
              open
              className="rounded-lg border"
            >
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium focus-visible:outline-ring">
                {origin ?? "Origin not recorded"}{" "}
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {rows.length} purchase {rows.length === 1 ? "lot" : "lots"} ·{" "}
                  {selected} selected
                </span>
              </summary>
              <div className="flex items-center gap-2 border-t px-4 py-2">
                <Checkbox
                  id={`origin-${rows[0]!.key}`}
                  aria-label={`Select all ${origin ?? "unassigned origin"} purchase lines`}
                  checked={
                    selected === rows.length
                      ? true
                      : selected > 0
                        ? "indeterminate"
                        : false
                  }
                  disabled={saving}
                  onCheckedChange={(checked) => {
                    const others = choices.filter(
                      (choice) => !rows.some((row) => row.key === choice.key),
                    );
                    updateChoices(
                      checked === true
                        ? [
                            ...others,
                            ...rows.map((row) => ({
                              key: row.key,
                              proposedCargoId:
                                choices.find((choice) => choice.key === row.key)
                                  ?.proposedCargoId ?? "",
                            })),
                          ]
                        : others,
                    );
                  }}
                />
                <label
                  className="flex min-h-10 cursor-pointer items-center text-xs"
                  htmlFor={`origin-${rows[0]!.key}`}
                >
                  Select all purchase lots in this origin
                </label>
              </div>
              <div className="overflow-x-auto border-t">
                <table className="w-full text-sm">
                  <caption className="sr-only">
                    {origin ?? "Unassigned origin"} historical purchases and
                    proposed coffees
                  </caption>
                  <thead className="bg-muted/30 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-medium">
                        Compare / historical coffee
                      </th>
                      <th className="p-3 font-medium">
                        Purchase contract / OCI
                      </th>
                      <th className="p-3 text-right font-medium">Purchased</th>
                      <th className="p-3 text-right font-medium">
                        Avg. paid / lb
                      </th>
                      <th className="p-3 font-medium">
                        Coffee proposed for contract
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const choice = choices.find(
                        (choice) => choice.key === row.key,
                      );
                      const label = `${row.productName} (${row.purchaseContractNumber ?? row.ociNumber ?? "unlinked lot"})`;
                      return (
                        <tr
                          key={row.key}
                          className={`border-t align-top ${choice ? "bg-muted/20" : ""}`}
                        >
                          <td className="min-w-56 p-3 pl-4">
                            <div className="flex items-start gap-3">
                              <Checkbox
                                id={`compare-${row.key}`}
                                aria-label={`Compare ${label}`}
                                className="mt-1"
                                checked={Boolean(choice)}
                                disabled={saving}
                                onCheckedChange={(checked) =>
                                  updateChoices(
                                    checked === true
                                      ? [
                                          ...choices,
                                          { key: row.key, proposedCargoId: "" },
                                        ]
                                      : choices.filter(
                                          (item) => item.key !== row.key,
                                        ),
                                  )
                                }
                              />
                              <label
                                htmlFor={`compare-${row.key}`}
                                className="flex min-h-10 cursor-pointer items-start font-medium"
                              >
                                {row.productName}
                              </label>
                            </div>
                          </td>
                          <td className="p-3 text-xs">
                            {row.purchaseContractNumber ?? "Not recorded"}
                            <span className="mt-1 block text-muted-foreground">
                              OCI: {row.ociNumber ?? "Not recorded"}
                            </span>
                          </td>
                          <td className="whitespace-nowrap p-3 text-right tabular-nums">
                            {number(row.purchasedPounds)} lb
                            <span className="mt-1 block text-xs text-muted-foreground">
                              {number(row.units)} units
                            </span>
                          </td>
                          <td className="p-3 text-right tabular-nums">
                            {formatCurrency(row.averagePricePerPound)}
                          </td>
                          <td className="min-w-80 p-3">
                            <NativeSelect
                              className="h-10"
                              aria-label={`Propose coffee for ${label}`}
                              value={choice?.proposedCargoId ?? ""}
                              disabled={!choice || saving}
                              onChange={(event) =>
                                updateChoices(
                                  choices.map((item) =>
                                    item.key === row.key
                                      ? {
                                          ...item,
                                          proposedCargoId: event.target.value,
                                        }
                                      : item,
                                  ),
                                )
                              }
                            >
                              <option value="">Choose a coffee and lot…</option>
                              {choice?.proposedCargoId &&
                                !options.some(
                                  (option) =>
                                    option.cargoId === choice.proposedCargoId,
                                ) && (
                                  <option value={choice.proposedCargoId}>
                                    Previously selected lot unavailable — choose
                                    again
                                  </option>
                                )}
                              {options.map((option) => (
                                <option
                                  key={option.cargoId}
                                  value={option.cargoId}
                                >
                                  {option.productName} ·{" "}
                                  {option.ociNumber
                                    ? `OCI ${option.ociNumber} · `
                                    : ""}
                                  {option.status === "in_spot"
                                    ? "In stock"
                                    : "Incoming"}{" "}
                                  · {number(option.availableBags)} bags
                                  {option.arrivalDate !== null &&
                                  option.status !== "in_spot"
                                    ? ` · Expected ${formatDate(option.arrivalDate)}`
                                    : ""}
                                </option>
                              ))}
                            </NativeSelect>
                            {choice && (
                              <p className="mt-2 text-xs text-muted-foreground tabular-nums">
                                3-month demand: {number(row.projectedPounds)} lb
                              </p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          );
        })
      )}
      {options.length === 0 && (
        <p role="status" className="text-sm text-muted-foreground">
          No in-stock or incoming coffee lots have available bags for
          comparison.
        </p>
      )}
      {missingHistory && (
        <p role="alert" className="text-sm text-destructive">
          A saved purchase line is outside this campaign window. Clear the
          selection and choose purchase lines again.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3 border-t pt-4">
        <p role="status" className="mr-auto text-sm tabular-nums">
          {choices.length} of 50 purchase lines selected
        </p>
        <Button
          className="min-h-10"
          variant="ghost"
          disabled={choices.length === 0 || saving}
          onClick={() => updateChoices([])}
        >
          Clear selection
        </Button>
        <Button
          className="min-h-10"
          disabled={!ready || loading || saving}
          onClick={calculate}
        >
          {loading ? "Calculating comparison…" : "Review comparison"}
        </Button>
      </div>
      {choices.length > 0 && missingProposal && (
        <p className="text-xs text-muted-foreground">
          Choose an available proposed coffee for every selected purchase line.
        </p>
      )}
      {previewError && (
        <p role="alert" className="text-sm text-destructive">
          {previewError}
        </p>
      )}
      {preview && (
        <CampaignClientReview
          prospect={preview}
          disclaimer={history.disclaimer}
          onSave={canManage ? saveComparison : undefined}
          saving={saving}
          saved={saved}
        />
      )}
      {!canManage && (
        <p className="text-xs text-muted-foreground">
          You can review comparisons. A campaign manager must save changes to a
          draft.
        </p>
      )}
    </div>
  );
}

type BuilderProps = {
  customerId: string;
  initialCampaignId?: string;
  onClose: () => void;
};

class ComparisonBoundary extends React.Component<
  { children: React.ReactNode; onClose: () => void },
  { error: Error | null }
> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="space-y-3 rounded-lg border p-4">
        <p role="alert" className="text-sm text-destructive">
          Unable to load the comparison: {message(this.state.error)}
        </p>
        <Button variant="outline" onClick={this.props.onClose}>
          Back to sales totals
        </Button>
      </div>
    );
  }
}

export function ContractComparisonBuilder(props: BuilderProps) {
  return (
    <ComparisonBoundary key={props.customerId} onClose={props.onClose}>
      <ComparisonWorkspace {...props} />
    </ComparisonBoundary>
  );
}

function ComparisonWorkspace({
  customerId,
  initialCampaignId,
  onClose,
}: BuilderProps) {
  const data = useQuery(api.contractSavings.list, {}) as
    { campaigns: SavingsCampaign[]; canManage: boolean } | undefined;
  const options = useQuery(api.contractSavings.proposalCoffees, {}) as
    ProposedCoffee[] | undefined;
  const [campaignId, setCampaignId] = React.useState(initialCampaignId ?? "");
  const selected = data?.campaigns.some(
    (campaign) => campaign._id === campaignId,
  );
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-64 flex-1 sm:max-w-md">
          <label
            htmlFor="comparison-campaign"
            className="mb-2 block text-sm font-medium"
          >
            Contract comparison campaign
          </label>
          <NativeSelect
            className="h-10"
            id="comparison-campaign"
            value={selected ? campaignId : ""}
            onChange={(event) => setCampaignId(event.target.value)}
            disabled={!data}
          >
            <option value="">
              {data ? "Choose a draft campaign…" : "Loading campaigns…"}
            </option>
            {data?.campaigns.map((campaign) => (
              <option key={campaign._id} value={campaign._id}>
                {campaign.name} · {campaign.lookbackMonths} months
              </option>
            ))}
          </NativeSelect>
        </div>
        <Button className="min-h-10" variant="outline" onClick={onClose}>
          Back to sales totals
        </Button>
      </div>
      {data?.campaigns.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Create a draft in{" "}
          <a
            className="underline underline-offset-4"
            href="/marketing/campaigns"
          >
            Marketing → Campaigns
          </a>{" "}
          to start a comparison.
        </p>
      )}
      {selected && data && (
        <ComparisonLines
          key={`${campaignId}-${customerId}`}
          campaignId={campaignId}
          customerId={customerId}
          canManage={data.canManage}
          options={options}
        />
      )}
    </div>
  );
}
